(() => {
  if (customElements.get('nbe-product-family')) return;

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('it')
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();

  const findDishwareSeries = (title, vendor) => {
    if (normalize(vendor) !== 'saturnia') return '';
    const cleaned = String(title || '').replace(/\s+/g, ' ').trim();
    const match = cleaned.match(/^(?:piatto\s+(?:da\s+portata|piano|fondo|frutta|dessert|pizza|da\s+pane|portata|ovale|rettangolare|quadrato|rotondo)|piattino\s+(?:caffe|caffè|te|tè|dessert)|insalatiera|coppetta|ciotola)\s+(.+)$/i);
    if (!match) return '';
    const family = match[1]
      .replace(/\s+(?:in\s+(?:porcellana|ceramica|gres|vetro|melamina|stoneware)|[Øø]|diametro\b|\d+(?:[.,]\d+)?\s*(?:cm|mm)\b).*$/i, '')
      .trim();
    return family.length >= 3 && family.length <= 65 ? family : '';
  };

  class NbeProductFamily extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      // The related-products carousel sits below the main product details.
      // Defer its network search until the critical product content is ready.
      const load = () => {
        if (this.isConnected) this.loadFamily();
      };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(load, { timeout: 1700 });
      } else {
        window.setTimeout(load, 450);
      }
    }

    disconnectedCallback() {
      this.dispatchEvent(new Event('nbe-series-dispose'));
    }

    async loadFamily() {
      const track = this.querySelector('[data-nbe-series-track]');
      const heading = this.querySelector('[data-nbe-series-heading]');
      const eyebrow = this.querySelector('[data-nbe-series-eyebrow]');
      const subtitle = this.querySelector('[data-nbe-series-subtitle]');
      if (!track || !heading) return;

      let loaded = await this.loadSameSeries(track);
      if (loaded < 2) {
        track.replaceChildren();
        loaded = await this.loadPredictiveSeries(track);
      }
      if (loaded >= 2) {
        const family = (this.dataset.series || '').trim() ||
          findDishwareSeries(this.dataset.title, this.dataset.vendor);
        heading.textContent = 'Tutti i prodotti della collezione ' + family;
        if (eyebrow) eyebrow.textContent = 'SCOPRI LA LINEA';
        if (subtitle) subtitle.textContent = 'Scopri gli altri articoli coordinati della stessa collezione.';
      } else {
        track.replaceChildren();
        loaded = await this.loadRelevantProducts(track);
        if (loaded >= 2) {
          heading.textContent = 'Articoli pertinenti';
          if (eyebrow) eyebrow.textContent = 'SCELTI PER TE';
          if (subtitle) subtitle.textContent = 'Altre soluzioni professionali affini a questo articolo.';
        }
      }

      if (loaded < 2) return;
      this.hidden = false;
      requestAnimationFrame(() => {
        this.setupMarquee(track);
        this.normalizePhotos(track);
      });
    }

    async loadSameSeries(track) {
      const vendor = this.dataset.vendor || '';
      const family = (this.dataset.series || '').trim() ||
        findDishwareSeries(this.dataset.title, vendor);
      if (!family || !vendor || !this.dataset.searchUrl) return 0;

      const familyNorm = normalize(family);
      const vendorNorm = normalize(vendor);
      if (familyNorm.length < 3) return 0;

      const seen = new Set();
      let shown = 0;
      const maxPages = 4;
      const origin = new URL(window.location.href);
      const previewTheme = origin.searchParams.get('preview_theme_id');

      try {
        for (let page = 1; page <= maxPages && shown < 12; page += 1) {
          const url = new URL(this.dataset.searchUrl, origin.origin);
          url.searchParams.set('q', family);
          url.searchParams.set('type', 'product');
          url.searchParams.set('view', 'nbe-series');
          url.searchParams.set('page', String(page));
          if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);

          const response = await fetch(url.toString(), { credentials: 'same-origin' });
          if (!response.ok) break;
          const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
          const container = documentResult.querySelector('[data-nbe-series-candidates]');
          if (!container) break;

          const tiles = Array.from(container.querySelectorAll('[data-product-title]'));
          for (const tile of tiles) {
            if (normalize(tile.dataset.vendor) !== vendorNorm) continue;
            const tileTitle = tile.dataset.productTitle || '';
            const actualFamily = findDishwareSeries(tileTitle, vendor);
            if (actualFamily && normalize(actualFamily) !== familyNorm) continue;

            if (!actualFamily) {
              const titleNorm = ' ' + normalize(tileTitle) + ' ';
              if (!titleNorm.includes(' ' + familyNorm + ' ')) continue;
              if (!this.dataset.series) continue;
            }

            const id = tile.dataset.productId || '';
            if (!id || id === this.dataset.productId || seen.has(id)) continue;
            // A product without a valid catalog photograph must not generate
            // the empty white cards seen on mobile.
            if (!tile.querySelector('img.nbe-product-family__img')) continue;
            seen.add(id);
            track.appendChild(tile);
            shown += 1;
          }

          const totalPages = Number(container.dataset.totalPages || '1');
          if (page >= totalPages || tiles.length === 0) break;
        }
      } catch (_) {
        return shown;
      }
      return shown;
    }

    imageUrlFor(product) {
      if (!product) return '';
      const options = [
        product.featured_image,
        product.featured_media?.preview_image,
        product.featured_media?.preview?.image,
        product.image,
        product.images?.[0],
        product.media?.[0]?.preview_image
      ];
      for (const option of options) {
        const raw = typeof option === 'string' ? option :
          (option?.url || option?.src || option?.originalSrc);
        if (!raw || typeof raw !== 'string') continue;
        try {
          const url = new URL(raw, window.location.origin);
          if (url.protocol === 'https:' || url.protocol === 'http:') return url.href;
        } catch (_) {}
      }
      return '';
    }

    async hydratePhoto(product) {
      if (this.imageUrlFor(product)) return product;
      const handle = String(product?.handle || '');
      if (!handle) return null;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = new URL(root.replace(/\/?$/, '/') + 'products/' +
          encodeURIComponent(handle) + '.js', window.location.origin);
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) return null;
        const full = await response.json();
        const image = this.imageUrlFor(full);
        if (!image) return null;
        return { ...product, featured_image: image };
      } catch (_) {
        return null;
      }
    }

    async loadPredictiveSeries(track) {
      const family = (this.dataset.series || '').trim() ||
        findDishwareSeries(this.dataset.title, this.dataset.vendor);
      if (!family || !this.dataset.vendor) return 0;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = new URL(root.replace(/\/?$/, '/') + 'search/suggest.json', window.location.origin);
        url.searchParams.set('q', family);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) return 0;
        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        const seen = new Set([this.dataset.productId]);
        for (const product of products) {
          if (normalize(product.vendor) !== normalize(this.dataset.vendor)) continue;
          const detected = findDishwareSeries(product.title, product.vendor);
          const valid = detected
            ? normalize(detected) === normalize(family)
            : (this.dataset.series && normalize(product.title).includes(normalize(family)));
          if (!valid || seen.has(String(product.id)) || product.available === false) continue;
          const hydrated = await this.hydratePhoto(product);
          if (!hydrated) continue;
          const tile = this.buildProductTile(hydrated);
          if (!tile) continue;
          track.appendChild(tile);
          seen.add(String(product.id));
        }
        return track.children.length;
      } catch (_) {
        return 0;
      }
    }

    buildProductTile(product) {
      const id = String(product?.id || '');
      const handle = String(product?.handle || '').trim();
      const imageUrl = this.imageUrlFor(product);
      if (!id || !handle || !imageUrl || id === this.dataset.productId) return null;

      const li = document.createElement('li');
      li.className = 'nbe-product-family__item';
      li.dataset.productId = id;
      li.dataset.productTitle = product.title || '';
      li.dataset.vendor = product.vendor || '';

      const link = document.createElement('a');
      link.className = 'nbe-product-family__link';
      const root = window.Shopify?.routes?.root || '/';
      link.href = root.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(handle);

      const media = document.createElement('span');
      media.className = 'nbe-product-family__media';
      const img = document.createElement('img');
      img.className = 'nbe-product-family__img';
      img.src = imageUrl;
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.sizes = '(max-width: 749px) 44vw, 280px';
      img.addEventListener('error', () => { li.hidden = true; }, { once: true });
      media.appendChild(img);

      const title = document.createElement('span');
      title.className = 'nbe-product-family__product-name';
      title.textContent = product.title || '';
      link.append(media, title);
      li.appendChild(link);
      return li;
    }

    async loadRelevantProducts(track) {
      const sourceTags = String(this.dataset.tags || '').split('|').map(normalize).filter(Boolean);
      const ownSubtags = sourceTags.filter((tag) => tag.startsWith('nv sub '));
      const ownMacros = sourceTags.filter((tag) => tag.startsWith('nv macro '));
      const type = normalize(this.dataset.productType);
      const vendor = normalize(this.dataset.vendor);
      const seen = new Set([this.dataset.productId]);
      const recUrl = this.dataset.recommendationsUrl;
      let candidates = [];

      // Shopify recommendations are the first choice: related products are
      // generated from merchandising, collection relations and product data.
      if (recUrl) {
        try {
          const url = new URL(recUrl, window.location.origin);
          url.searchParams.set('product_id', this.dataset.productId);
          url.searchParams.set('intent', 'related');
          url.searchParams.set('limit', '10');
          const response = await fetch(url.toString(), { credentials: 'same-origin' });
          if (response.ok) {
            const data = await response.json();
            candidates = Array.isArray(data.products) ? data.products : [];
          }
        } catch (_) {}
      }

      const score = (product, recommended = false) => {
        const tags = (product.tags || []).map(normalize);
        const matchSub = ownSubtags.some((tag) => tags.includes(tag));
        const matchMacro = ownMacros.some((tag) => tags.includes(tag));
        const matchType = type && normalize(product.type) === type;
        const matchVendor = vendor && normalize(product.vendor) === vendor;
        return (recommended ? 2 : 0) +
          (matchSub ? 8 : 0) +
          (matchType ? 6 : 0) +
          (matchMacro ? 3 : 0) +
          (matchVendor ? 1 : 0);
      };

      const ranked = candidates
        .filter((product) => product && product.available !== false && String(product.id) !== this.dataset.productId)
        .map((product) => ({ product, rank: score(product, true) }))
        .sort((a, b) => b.rank - a.rank);
      const eligible = ranked.filter(({ rank }) => rank >= 5).slice(0, 10);
      const withPhotos = await Promise.all(
        eligible.map(async ({ product }) => this.hydratePhoto(product))
      );
      for (const product of withPhotos) {
        if (!product || seen.has(String(product.id))) continue;
        const tile = this.buildProductTile(product);
        if (!tile) continue;
        seen.add(String(product.id));
        track.appendChild(tile);
        if (track.children.length >= 10) break;
      }
      if (track.children.length >= 4) return track.children.length;

      // When recommendations are sparse, search the same professional
      // product type, not merely the same brand.
      if (!this.dataset.searchUrl) return track.children.length;
      const searchTerm = this.dataset.productType?.trim() ||
        (this.dataset.title || '').split(/\s+/).slice(0, 3).join(' ');
      if (searchTerm.length < 3) return track.children.length;
      const origin = new URL(window.location.href);
      const previewTheme = origin.searchParams.get('preview_theme_id');
      try {
        for (let page = 1; page <= 3 && track.children.length < 10; page += 1) {
          const url = new URL(this.dataset.searchUrl, origin.origin);
          url.searchParams.set('q', searchTerm);
          url.searchParams.set('type', 'product');
          url.searchParams.set('view', 'nbe-series');
          url.searchParams.set('page', String(page));
          if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
          const response = await fetch(url.toString(), { credentials: 'same-origin' });
          if (!response.ok) break;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const result = html.querySelector('[data-nbe-series-candidates]');
          if (!result) break;
          const tiles = Array.from(result.querySelectorAll('[data-product-id]'));
          const prioritized = tiles
            .filter((item) => {
              const tags = String(item.dataset.productTags || '').split('|').map(normalize);
              const sub = ownSubtags.some((tag) => tags.includes(tag));
              const macro = ownMacros.some((tag) => tags.includes(tag));
              const exactType = normalize(item.dataset.productType) === type;
              const sameVendor = normalize(item.dataset.vendor) === vendor;
              item.dataset.nbeRank = String((sub ? 8 : 0) + (exactType ? 6 : 0) + (macro ? 3 : 0) + (sameVendor ? 1 : 0));
              const matchingTitle = normalize(item.dataset.productTitle)
                .split(' ').filter(Boolean).some((word) => word.length >= 5 &&
                  normalize(this.dataset.title).split(' ').includes(word));
              return sub || exactType || (macro && sameVendor && matchingTitle);
            })
            .sort((a, b) => Number(b.dataset.nbeRank) - Number(a.dataset.nbeRank));
          for (const tile of prioritized) {
            const id = tile.dataset.productId;
            if (!id || seen.has(id) || id === this.dataset.productId) continue;
            if (!tile.querySelector('img.nbe-product-family__img')) continue;
            seen.add(id);
            tile.removeAttribute('data-nbe-rank');
            track.appendChild(tile);
            if (track.children.length >= 10) break;
          }
          if (page >= Number(result.dataset.totalPages || 1)) break;
        }
      } catch (_) {}
      return track.children.length;
    }

    setupMarquee(track) {
      if (track.dataset.marqueeReady === 'true') return;
      const originals = Array.from(track.children);
      if (originals.length < 2) return;

      const duplicate = (item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((node) => node.setAttribute('tabindex', '-1'));
        return clone;
      };

      let filler = 0;
      while (track.scrollWidth < track.clientWidth * 1.65 && filler < 12) {
        originals.forEach((item) => track.appendChild(duplicate(item)));
        filler += 1;
      }

      const cycleItems = Array.from(track.children);
      for (let copy = 0; copy < 2; copy += 1) {
        cycleItems.forEach((item) => track.appendChild(duplicate(item)));
      }

      const firstCopy = track.children[cycleItems.length];
      const cycleWidth = firstCopy
        ? firstCopy.offsetLeft - cycleItems[0].offsetLeft
        : 0;
      if (cycleWidth <= 0) return;

      track.dataset.marqueeReady = 'true';
      track.classList.add('nbe-product-family__track--moving');
      track.scrollLeft = cycleWidth;

      let lastTime = 0;
      let requestId = 0;
      let pointerHeld = false;
      let visible = true;
      let resumeAt = 0;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const mobile = window.matchMedia('(max-width: 749px)');

      const wrap = () => {
        const x = track.scrollLeft;
        if (x >= cycleWidth * 2) {
          track.scrollLeft = x - cycleWidth;
        } else if (x <= cycleWidth * 0.6) {
          track.scrollLeft = x + cycleWidth;
        }
      };

      const animate = (now) => {
        requestId = 0;
        if (!this.isConnected || !visible || document.hidden || reducedMotion.matches) {
          lastTime = 0;
          return;
        }

        const elapsed = lastTime ? Math.min(Math.max(now - lastTime, 0), 50) : 0;
        lastTime = now;
        if (!pointerHeld && now > resumeAt) {
          const pixelsPerSecond = mobile.matches ? 100 : 115;
          track.scrollLeft += (elapsed * pixelsPerSecond) / 1000;
        }

        if (!pointerHeld) wrap();
        requestId = requestAnimationFrame(animate);
      };
      const start = () => {
        if (requestId || !this.isConnected || !visible || document.hidden || reducedMotion.matches) return;
        lastTime = 0;
        requestId = requestAnimationFrame(animate);
      };
      const stop = () => {
        if (requestId) cancelAnimationFrame(requestId);
        requestId = 0;
        lastTime = 0;
      };

      const onPointerDown = () => {
        pointerHeld = true;
      };
      const onPointerRelease = () => {
        if (!pointerHeld) return;
        pointerHeld = false;
        resumeAt = performance.now() + 280;
      };

      track.addEventListener('pointerdown', onPointerDown, { passive: true });
      window.addEventListener('pointerup', onPointerRelease, { passive: true });
      window.addEventListener('pointercancel', onPointerRelease, { passive: true });

      const observer = new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible) start(); else stop();
      }, { rootMargin: '150px' });

      const onPageVisibility = () => {
        if (document.hidden) stop(); else start();
      };
      const onMotionPreference = () => {
        if (reducedMotion.matches) stop(); else start();
      };
      observer.observe(this);
      document.addEventListener('visibilitychange', onPageVisibility);
      if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionPreference);
      const cleanup = () => {
        stop();
        observer.disconnect();
        document.removeEventListener('visibilitychange', onPageVisibility);
        if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', onMotionPreference);
        track.removeEventListener('pointerdown', onPointerDown);
        window.removeEventListener('pointerup', onPointerRelease);
        window.removeEventListener('pointercancel', onPointerRelease);
      };

      this.addEventListener('nbe-series-dispose', cleanup, { once: true });
    }

    normalizePhotos(track) {
      const items = Array.from(track.querySelectorAll('[data-product-id]'));
      const unique = new Map();
      items.forEach((item) => {
        const id = item.dataset.productId;
        const img = item.querySelector('img');
        if (id && img && !unique.has(id)) unique.set(id, img);
      });

      unique.forEach((img, id) => {
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.onload = () => {
          try {
            const w = 180;
            const h = Math.max(50, Math.round(180 * probe.naturalHeight / probe.naturalWidth));
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const context = canvas.getContext('2d', { willReadFrequently: true });
            if (!context) return;
            context.drawImage(probe, 0, 0, w, h);
            const pixels = context.getImageData(0, 0, w, h).data;
            const corners = [0, (w - 1) * 4, (h - 1) * w * 4, (h * w - 1) * 4];
            const bg = [0, 1, 2].map((channel) =>
              corners.reduce((sum, index) => sum + pixels[index + channel], 0) / 4
            );

            let minX = w, minY = h, maxX = -1, maxY = -1, count = 0;
            for (let y = 0; y < h; y += 1) {
              for (let x = 0; x < w; x += 1) {
                const i = (y * w + x) * 4;
                if (pixels[i + 3] < 40) continue;
                const difference = Math.max(
                  Math.abs(pixels[i] - bg[0]),
                  Math.abs(pixels[i + 1] - bg[1]),
                  Math.abs(pixels[i + 2] - bg[2])
                );
                if (difference < 22) continue;
                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
                minY = Math.min(minY, y);
                maxY = Math.max(maxY, y);
                count += 1;
              }
            }
            if (count < w * h * 0.007 || maxX < 0) return;
            const media = img.closest('.nbe-product-family__media');
            if (!media) return;

            const boxRatio = media.clientWidth / Math.max(1, media.clientHeight);
            const imageRatio = probe.naturalWidth / probe.naturalHeight;
            const fitWidth = imageRatio > boxRatio ? 1 : imageRatio / boxRatio;
            const fitHeight = imageRatio > boxRatio ? boxRatio / imageRatio : 1;
            const contentFraction = Math.max(
              (maxX - minX + 1) / w * fitWidth,
              (maxY - minY + 1) / h * fitHeight
            );
            const scale = Math.max(0.94, Math.min(2.5, 0.82 / Math.max(0.05, contentFraction)));
            items.forEach((item) => {
              if (item.dataset.productId === id) {
                const image = item.querySelector('.nbe-product-family__img');
                if (image) image.style.setProperty('--nbe-photo-scale', scale.toFixed(3));
              }
            });
          } catch (_) {
            // Remote images without CORS support retain the safe object-fit fallback.
          }
        };
        probe.onerror = () => {};
        probe.src = img.currentSrc || img.src;
      });
    }
  }

  customElements.define('nbe-product-family', NbeProductFamily);
})();